import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const WARN_S = 120;
const HEARTBEAT_EVERY_MS = 60 * 1000;
const LAST_KEY = "sessionLastActivity";
const EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.max(0, Math.floor(s % 60))).padStart(2, "0")}`;

export const SessionManager = ({ session, onExpire, onLogout }) => {
  const [info, setInfo] = useState(session);
  const [remaining, setRemaining] = useState(null);
  const [warnKind, setWarnKind] = useState(null);
  const lastBeat = useRef(0);
  const warnRef = useRef(null);
  warnRef.current = warnKind;

  const beat = useCallback(async () => {
    lastBeat.current = Date.now();
    try { const { data } = await api.post("/auth/session/heartbeat"); setInfo(data); } catch { /* 401 handled globally */ }
  }, []);

  useEffect(() => {
    if (!info) api.get("/auth/session").then(({ data }) => setInfo(data)).catch(() => {});
    if (!localStorage.getItem(LAST_KEY)) localStorage.setItem(LAST_KEY, String(Date.now()));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const onActivity = () => {
      if (warnRef.current) return;
      localStorage.setItem(LAST_KEY, String(Date.now()));
      if (Date.now() - lastBeat.current > HEARTBEAT_EVERY_MS) beat();
    };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
  }, [beat]);

  useEffect(() => {
    if (!info) return undefined;
    const t = setInterval(() => {
      const now = Date.now();
      const last = Number(localStorage.getItem(LAST_KEY) || now);
      const idleLeft = info.idle_timeout_s - (now - last) / 1000;
      const absLeft = (new Date(info.expires_at).getTime() - now) / 1000;
      const left = Math.min(idleLeft, absLeft);
      setRemaining(left);
      if (left <= 0) { clearInterval(t); onExpire(); return; }
      if (left <= WARN_S) setWarnKind(absLeft <= idleLeft ? "absolute" : "idle");
      else if (warnRef.current) setWarnKind(null);
    }, 1000);
    return () => clearInterval(t);
  }, [info, onExpire]);

  const stay = async () => {
    localStorage.setItem(LAST_KEY, String(Date.now()));
    setWarnKind(null);
    await beat();
  };

  return (
    <AlertDialog open={!!warnKind}>
      <AlertDialogContent className="w-[calc(100%-2rem)] max-w-md rounded-sm" data-testid="session-expiry-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{warnKind === "absolute" ? "Your session is ending" : "Are you still there?"}</AlertDialogTitle>
          <AlertDialogDescription>
            {warnKind === "absolute"
              ? "For security, sessions have a maximum length. You'll need to sign in again in "
              : "You've been inactive for a while. For security you'll be signed out in "}
            <span className="font-semibold tabular-nums text-slate-900" data-testid="session-expiry-countdown">{fmt(remaining || 0)}</span>.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onLogout} data-testid="session-expiry-logout">Sign out now</AlertDialogCancel>
          {warnKind === "idle" && (
            <AlertDialogAction onClick={stay} data-testid="session-expiry-stay">Stay signed in</AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
